import { LocalDateFieldModel, NumberFieldModel, StringFieldModel } from "@/common/commonForms";
import { isPrintableKey } from "@/utils/keyboardEvents";
import { TableColumnValue } from "./DataTable";
import { compact, defer, groupBy, omit } from "lodash";
import { SyntheticEvent } from "react";

type Cell = { rowId: string; colName: string };

export type EditableDataTableState = {
  selectedCell?: Cell;
  editorModel?: TableColumnValue;
};

export type Effect<F extends (...args: never[]) => EditableDataTableHandlerResult | void> = [
  F,
  ...Parameters<F>,
];

function fx<F extends (...args: never[]) => EditableDataTableHandlerResult | void>(
  fn: F,
  ...args: Parameters<F>
): Effect<F> {
  return [fn, ...args];
}

export type EditableDataTableHandlerResult = {
  state?: EditableDataTableState;
  // oxlint-disable-next-line typescript/no-explicit-any
  effects?: Effect<any>[];
};

export type EditableDataTableContext = {
  findCell(cell: Cell, deltaX: number, deltaY: number): Cell | undefined;
  getValue(cell: Cell): TableColumnValue;
  isReadonly(cell: Cell): boolean;
  container: HTMLElement | null;
};

export function handleFocusCell(
  context: EditableDataTableContext,
  cell: Cell,
): EditableDataTableHandlerResult | undefined {
  return {
    effects: [fx(focusCell, context, cell)],
  };
}

export function handleFocus(
  state: EditableDataTableState,
  event: FocusEvent | React.FocusEvent,
): EditableDataTableHandlerResult | undefined {
  const td = (event.target as HTMLElement).closest("td");
  const rowId = td?.dataset.rowId;
  const colName = td?.dataset.colName;
  if (
    rowId &&
    colName &&
    (!state.selectedCell ||
      state.selectedCell.rowId !== rowId ||
      state.selectedCell.colName !== colName)
  ) {
    return {
      state: { selectedCell: { rowId: td!.dataset.rowId!, colName: td!.dataset.colName! } },
      effects: [fx(scrollIntoView, td)],
    };
  }
}

export function handleBlur(
  context: EditableDataTableContext,
  state: EditableDataTableState,
  event: FocusEvent | React.FocusEvent,
): EditableDataTableHandlerResult | undefined {
  if (!state.selectedCell) {
    return;
  }
  const target = event.target as HTMLElement | null;
  const relatedTarget = event.relatedTarget as HTMLElement | null;
  const td = target?.closest(
    `[data-row-id='${state.selectedCell.rowId}'][data-col-name='${state.selectedCell.colName}']`,
  );
  const relatedTd = relatedTarget?.closest("[data-row-id][data-col-name]");

  const popoverTrigger = td?.querySelector("[aria-controls][aria-expanded=true]");
  const popover = popoverTrigger
    ? relatedTarget?.closest(`[id='${popoverTrigger.getAttribute("aria-controls")}']`)
    : undefined;

  if (td?.contains(relatedTarget) || popover) {
    return;
  }

  if (td) {
    return {
      state: {},
      effects: state.editorModel
        ? compact([
            fx(commitEditor, context, state),
            relatedTd && fx(stopEventPropagation, event), // Prevent interference with focus traps in dialogs
          ])
        : undefined,
    };
  }
}

export function handleClick(
  context: EditableDataTableContext,
  state: EditableDataTableState,
): EditableDataTableHandlerResult | undefined {
  if (state.selectedCell && !state.editorModel) {
    return { state: { ...state, editorModel: initEditor(context, state.selectedCell) } };
  }
}

export function handleKeyDown(
  context: EditableDataTableContext,
  state: EditableDataTableState,
  event: KeyboardEvent | React.KeyboardEvent,
): EditableDataTableHandlerResult | undefined {
  if (!state.selectedCell) {
    return;
  }

  switch (true) {
    case !state.editorModel:
      switch (true) {
        case event.key === "Enter": {
          event.preventDefault();
          event.stopPropagation();
          const updatedState = { ...state, editorModel: initEditor(context, state.selectedCell) };
          return {
            state: updatedState,
            effects: !updatedState.editorModel
              ? [fx(activateInteractiveComponent, context, updatedState)]
              : undefined,
          };
        }

        case isPrintableKey(event): {
          event.preventDefault();
          event.stopPropagation();
          return {
            state: { ...state, editorModel: initEditor(context, state.selectedCell, event.key) },
          };
        }

        case event.key === "ArrowUp":
          event.preventDefault();
          return { effects: [fx(selectCell, context, state, 0, -1)] };

        case event.key === "ArrowDown":
          event.preventDefault();
          return { effects: [fx(selectCell, context, state, 0, 1)] };

        case event.key === "ArrowLeft":
          event.preventDefault();
          return { effects: [fx(selectCell, context, state, -1, 0)] };

        case event.key === "ArrowRight":
          event.preventDefault();
          return { effects: [fx(selectCell, context, state, 1, 0)] };

        case event.key === "Delete":
        case event.key === "Backspace":
          event.preventDefault();
          return { effects: [fx(clearCell, context, state)] };

        default:
          return;
      }

    default:
      switch (event.key) {
        case "Enter":
          return isInPopover(context, state) || isInTextArea(state)
            ? undefined
            : {
                effects: [
                  fx(commitEditor, context, state), //
                  fx(selectCell, context, state, 0, 1),
                ],
              };
        case "ArrowDown":
        case "ArrowUp":
          return isInPopover(context, state) || isInTextArea(state)
            ? undefined
            : {
                effects: [
                  fx(commitEditor, context, state),
                  fx(selectCell, context, state, 0, event.key === "ArrowUp" ? -1 : 1),
                ],
              };

        case "Escape": {
          const updatedState: EditableDataTableState = { ...state, editorModel: undefined };
          return isInPopover(context, state)
            ? undefined
            : {
                state: updatedState,
                effects: [fx(selectCell, context, updatedState)],
              };
        }

        default:
          return;
      }
  }
}

export function handlePopoverClose(
  context: EditableDataTableContext,
  state: EditableDataTableState,
): EditableDataTableHandlerResult {
  return {
    state: { ...state, editorModel: undefined },
    effects: [[deferred, selectCell, context, state]],
  };
}

// oxlint-disable-next-line typescript/no-explicit-any
function deferred(f: (...args: any) => void, ...args: any) {
  defer(() => f(...args));
}

function isInPopover(context: EditableDataTableContext, state: EditableDataTableState): boolean {
  if (!state.selectedCell) {
    return false;
  }
  return !!context.container?.querySelector(
    `td[data-row-id='${state.selectedCell.rowId}'][data-col-name='${state.selectedCell.colName}'] [aria-controls][aria-expanded='true']`,
  );
}

function isInTextArea(state: EditableDataTableState): boolean {
  return Array.isArray(state.editorModel) && state.editorModel[0] === "mtextarea";
}

export function initEditor(
  context: EditableDataTableContext,
  cell: Cell,
  initialValue?: string,
): TableColumnValue | undefined {
  if (context.isReadonly(cell)) {
    return;
  }

  const underlyingModel = context.getValue(cell);

  if (Array.isArray(underlyingModel)) {
    switch (underlyingModel[0]) {
      case "mtext-input":
        if (underlyingModel[1].model instanceof StringFieldModel) {
          const m = new StringFieldModel({
            ...underlyingModel[1].model.params,
            onChange: undefined,
          });
          m.setInputValue(initialValue ?? underlyingModel[1].model.inputValue);
          return ["mtext-input", { ...underlyingModel[1], model: m }, underlyingModel[2]];
        } else {
          const m = new NumberFieldModel({
            ...underlyingModel[1].model.params,
            onChange: undefined,
          });
          m.setLocale(underlyingModel[1].model.locale);
          m.setInputValue(initialValue ?? underlyingModel[1].model.inputValue);
          return ["mtext-input", { ...underlyingModel[1], model: m }, underlyingModel[2]];
        }

      case "mtextarea": {
        const m = new StringFieldModel({
          ...underlyingModel[1].model.params,
          onChange: undefined,
        });
        m.setInputValue(initialValue ?? underlyingModel[1].model.inputValue);
        return ["mtextarea", { ...underlyingModel[1], model: m }, underlyingModel[2]];
      }

      case "mdate-input": {
        const m = new LocalDateFieldModel(omit(underlyingModel[1].model.params, "onChange"));
        m.setInputValue(initialValue ?? underlyingModel[1].model.inputValue);
        return ["mdate-input", { ...underlyingModel[1], model: m }, underlyingModel[2]];
      }

      case "mselect":
      case "select":
        return underlyingModel;
    }
  }
}

export function activateInteractiveComponent(
  context: EditableDataTableContext,
  state: EditableDataTableState,
) {
  if (!state.selectedCell) {
    return;
  }
  const value = context.getValue(state.selectedCell);
  if (Array.isArray(value)) {
    switch (value[0]) {
      case "icon-button":
      case "info":
      case "link":
        context.container
          ?.querySelector<HTMLElement>(
            `[data-row-id='${state.selectedCell.rowId}'][data-col-name='${state.selectedCell.colName}'] button`,
          )
          ?.click();
        break;
    }
  }
}

export function stopEventPropagation(event: UIEvent | SyntheticEvent) {
  event.stopPropagation();
}

export function commitEditor(context: EditableDataTableContext, state: EditableDataTableState) {
  if (state.selectedCell && state.editorModel && Array.isArray(state.editorModel)) {
    const underlyingModel = context.getValue(state.selectedCell);
    switch (state.editorModel[0]) {
      case "mtextarea":
      case "mtext-input":
      case "mdate-input": {
        const m = underlyingModel as typeof state.editorModel;
        m[1].model.setAndCommitInputValue(state.editorModel[1].model.inputValue);
        m[2]?.onCommit?.();
        break;
      }
    }
  }
}

export function clearCell(context: EditableDataTableContext, state: EditableDataTableState) {
  const underlyingModel = state.selectedCell ? context.getValue(state.selectedCell) : undefined;
  if (Array.isArray(underlyingModel)) {
    switch (underlyingModel[0]) {
      case "mtextarea":
      case "mtext-input":
      case "mdate-input":
        underlyingModel[1].model.setAndCommitInputValue("");
        underlyingModel[2]?.onCommit?.();
        break;
    }
  }
}

export function selectCell(
  context: EditableDataTableContext,
  state: EditableDataTableState,
  deltaX: number = 0,
  deltaY: number = 0,
) {
  const cell = state.selectedCell
    ? context.findCell(state.selectedCell, deltaX, deltaY)
    : undefined;
  if (cell) {
    focusCell(context, cell);
  }
}

export function focusCell(context: EditableDataTableContext, cell: Cell) {
  const targetTd = context.container?.querySelector<HTMLElement>(
    `td[data-row-id='${cell?.rowId}'][data-col-name='${cell?.colName}']`,
  );
  if (targetTd) {
    (targetTd.querySelector<HTMLElement>("[tabindex='0']") ?? targetTd).focus();
  }
}

export function scrollIntoView(elem: HTMLElement) {
  elem.scrollIntoView({ block: "nearest", inline: "nearest" });
}

export type IndexedRow<T> = { row: T; index: number };

export type TableRowEntry<T> =
  | ({ type: "row" } & IndexedRow<T>)
  | { type: "group"; groupId: string; rows: IndexedRow<T>[] };

export function groupRows<T>(
  rows: T[],
  getRowGroupId?: (row: T) => string | undefined,
): TableRowEntry<T>[] {
  const indexedRows = rows.map((row, index) => ({ row, index }));
  if (!getRowGroupId) {
    return indexedRows.map((indexed) => ({ type: "row", ...indexed }));
  }
  const rowsByGroup = groupBy(
    indexedRows.filter((indexed) => getRowGroupId(indexed.row) !== undefined),
    (indexed) => getRowGroupId(indexed.row),
  );
  return indexedRows.flatMap<TableRowEntry<T>>((indexed) => {
    const groupId = getRowGroupId(indexed.row);
    if (groupId === undefined) {
      return [{ type: "row", ...indexed }];
    }
    const groupRows = rowsByGroup[groupId];
    if (groupRows[0] !== indexed) {
      return [];
    }
    return groupRows.length === 1
      ? [{ type: "row", ...indexed }]
      : [{ type: "group", groupId, rows: groupRows }];
  });
}
